/**
 * Kiosk mode flag (TCGMA-176).
 *
 * The shop runs as a physical touchscreen kiosk (Chromium --kiosk on a mini-PC +
 * 23" capacitive panel). Kiosk-only UI — chiefly the on-screen keyboard — hangs
 * off this flag so normal web visitors never see it.
 *
 * Set with `?kiosk=1` on the entry URL (a query param, not a hostname check, so
 * the kiosk URL can be flipped without DNS changes).
 *
 * ── WHY THIS IS AN IN-MEMORY FLAG, NOT sessionStorage ──
 * The first version made sessionStorage the source of truth and returned false
 * if it threw. That is backwards for a KIOSK: a locked-down kiosk browser is
 * exactly the environment where DOM storage gets blocked (ephemeral profile,
 * form-data disabled, site data cleared between sessions). The keyboard then
 * silently disabled itself — deployed, reachable, and dead — which is what admin
 * found on the real panel.
 *
 * So: a module-level variable is authoritative. It survives everything the flag
 * actually needs to survive, because SPA navigation (inventory → card detail →
 * join) never reloads the page. sessionStorage is now only a best-effort nicety
 * for a full reload, and can fail freely without turning the keyboard off.
 *
 * ── WHY THE URL GUARD ──
 * The in-memory flag dies on a RELOAD, and the only place a reload can recover it
 * from — on a browser where storage is blocked — is the address bar. But the
 * address bar was NOT safe: /shop/inventory mirrors its filter state to the URL by
 * rebuilding the query string from the filters alone (see its replaceState
 * effect), which dropped every other param. So `?kiosk=1` was erased seconds after
 * boot, and the erasing write is triggered BY TYPING IN THE SEARCH BOX — i.e. the
 * customer using the keyboard is what destroyed the flag that renders the
 * keyboard. On the trade-show profile (storage blocked, param gone) a single
 * reload would leave the kiosk permanently unusable with no way back short of
 * relaunching Chromium. Admin caught this on the panel before the show.
 *
 * So in kiosk mode we pin the param to every history write. A guard rather than
 * fixing the one known writer: a missed writer is a dead kiosk in the field, and
 * the router's own navigations strip unknown params too. It is installed ONLY in
 * kiosk mode, so normal visitors' URLs are untouched.
 *
 * Read from window.location (NOT the router's search params): TanStack routes
 * declare validateSearch and would strip an unrecognized param before any route
 * component ran.
 */

const KEY = "tcg-web.kiosk";
const PARAM = "kiosk";

/** Authoritative. Cannot be blocked by browser policy, unlike storage. */
let kioskFlag = false;

/** Best-effort only — never let a storage failure decide whether kiosk mode is on. */
function readStickyHint(): boolean {
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

function writeStickyHint(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(KEY, "1");
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage blocked by kiosk policy — irrelevant, the in-memory flag stands */
  }
}

/**
 * Re-attach ?kiosk=1 to a same-origin URL a history write is about to install.
 * Returns the input untouched for anything cross-origin.
 */
function withKioskParam(url: string | URL): string | URL {
  try {
    const next = new URL(String(url), window.location.href);
    if (next.origin !== window.location.origin) return url;
    next.searchParams.set(PARAM, "1");
    return next.pathname + next.search + next.hash;
  } catch {
    return url; // unparseable — let the browser deal with it as it would have
  }
}

/**
 * Pin the kiosk param to every same-origin history write, so the address bar
 * always carries the flag a reload needs. See the header note.
 */
function installUrlGuard(): void {
  for (const name of ["pushState", "replaceState"] as const) {
    const original = History.prototype[name];
    History.prototype[name] = function (
      this: History,
      state: unknown,
      unused: string,
      url?: string | URL | null,
    ) {
      // A null/undefined url means "keep the current URL" — nothing to preserve.
      return original.call(
        this,
        state,
        unused,
        url == null ? url : withKioskParam(url),
      );
    };
  }

  // We may have BOOTED from an already-stripped URL (recovered via the storage
  // hint). Put the param back now so the very next reload can recover too.
  const here = new URL(window.location.href);
  if (here.searchParams.get(PARAM) !== "1") {
    here.searchParams.set(PARAM, "1");
    History.prototype.replaceState.call(
      window.history,
      window.history.state,
      "",
      here.pathname + here.search + here.hash,
    );
  }
}

/** Capture ?kiosk=… from the entry URL. Call once, early, in main.tsx. */
export function initKioskMode(): boolean {
  let flag: string | null = null;
  try {
    flag = new URLSearchParams(window.location.search).get("kiosk");
  } catch {
    /* malformed query — fall through to the hint */
  }

  if (flag === "1" || flag === "true") {
    kioskFlag = true;
  } else if (flag === "0" || flag === "false") {
    kioskFlag = false; // explicit escape hatch for testing
  } else {
    // No param on this load (e.g. a full reload onto a deep link) — fall back to
    // the sticky hint if storage happens to work.
    kioskFlag = readStickyHint();
  }

  writeStickyHint(kioskFlag);

  // Keep the address bar carrying the flag — the only channel that survives a
  // reload on a storage-blocked kiosk profile.
  if (kioskFlag) installUrlGuard();

  // Diagnostic hook: lets the kiosk be debugged from a console/CDP trace without
  // guessing. Read-only, and says nothing a visitor can't already infer from the
  // URL they were sent to.
  try {
    (window as unknown as Record<string, unknown>).__tcgKiosk = kioskFlag;
  } catch {
    /* non-fatal */
  }

  return kioskFlag;
}

export function isKioskMode(): boolean {
  return kioskFlag;
}
